import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Download, Package } from "lucide-react";
import api, { formatPrice, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function OrderConfirmation() {
    const { orderNumber } = useParams();
    const { user } = useAuth();
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (user) {
            api.get(`/orders/${orderNumber}`).then(({ data }) => setOrder(data.order)).catch(() => setError(true));
        }
    }, [orderNumber, user]);

    const downloadInvoice = () => {
        const token = localStorage.getItem("elvosh_token");
        fetch(`${BACKEND_URL}/api/orders/${orderNumber}/invoice`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `ELVOSH-${orderNumber}.pdf`; a.click();
                URL.revokeObjectURL(url);
            });
    };

    const status = order?.payment?.status;
    const isPaid = status === "paid";
    const isCod = status === "cod_pending";
    const isFailed = status === "failed";

    return (
        <div className="mx-auto max-w-2xl px-5 py-16 text-center">
            {isFailed ? (
                <XCircle className="h-16 w-16 mx-auto text-destructive" strokeWidth={1} />
            ) : isPaid || isCod ? (
                <CheckCircle2 className="h-16 w-16 mx-auto text-gold" strokeWidth={1} />
            ) : (
                <Clock className="h-16 w-16 mx-auto text-gold" strokeWidth={1} />
            )}

            <h1 className="font-serif text-4xl mt-6">
                {isFailed ? "Payment Failed" : isCod ? "Order Placed!" : isPaid ? "Thank You!" : "Order Received"}
            </h1>
            <p className="text-muted-foreground mt-3">
                {isFailed ? "Your payment could not be verified. No amount has been charged for this order." :
                 isCod ? "Your Cash on Delivery order has been placed successfully." :
                 isPaid ? "Your payment was verified and your order is confirmed." :
                 "We are confirming your payment status."}
            </p>

            <div className="bg-cream-soft p-6 mt-8 text-left" data-testid="order-summary">
                <div className="flex justify-between items-center pb-4 border-b border-[#E8DFD5]">
                    <div>
                        <p className="eyebrow">Order ID</p>
                        <p className="font-serif text-xl mt-1" data-testid="order-number">{orderNumber}</p>
                    </div>
                    <span className={`text-xs uppercase tracking-wider px-3 py-1.5 ${isPaid ? "bg-gold/15 text-gold" : isFailed ? "bg-destructive/10 text-destructive" : "bg-ink/10"}`}>
                        {isPaid ? "Paid" : isCod ? "COD / Pending" : isFailed ? "Failed" : "Pending"}
                    </span>
                </div>

                {order ? (
                    <div className="mt-4 space-y-3 text-sm">
                        {order.items.map((it, i) => (
                            <div key={i} className="flex justify-between"><span>{it.name} × {it.qty}</span><span>{formatPrice(it.line_total)}</span></div>
                        ))}
                        <div className="flex justify-between font-semibold border-t border-[#E8DFD5] pt-3"><span>Total</span><span>{formatPrice(order.total)}</span></div>
                        <div className="text-muted-foreground pt-2">
                            <p>{order.customer.name} · {order.customer.phone}</p>
                            <p>{order.address.line}, {order.address.city}, {order.address.state} - {order.address.pincode}</p>
                        </div>
                    </div>
                ) : (
                    <p className="mt-4 text-sm text-muted-foreground">{user ? "Loading order details…" : "Log in to view full order details."}</p>
                )}
            </div>

            <div className="flex flex-wrap gap-3 justify-center mt-8">
                {order && (isPaid || isCod) && (
                    <button data-testid="download-invoice" onClick={downloadInvoice} className="flex items-center gap-2 border border-ink px-6 py-3 text-sm uppercase tracking-[0.15em] hover:bg-ink hover:text-cream transition-colors"><Download className="h-4 w-4" /> Invoice</button>
                )}
                <Link to="/account" className="flex items-center gap-2 border border-ink px-6 py-3 text-sm uppercase tracking-[0.15em] hover:bg-ink hover:text-cream transition-colors"><Package className="h-4 w-4" /> My Orders</Link>
                <Link to="/shop" className="bg-gold text-white px-6 py-3 text-sm uppercase tracking-[0.15em] hover:bg-gold-hover transition-colors">Continue Shopping</Link>
            </div>
        </div>
    );
}
